import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { formatCount, formatCurrency } from '../lib/format';
import { EmptyState, ErrorNote, Panel, Spinner, Tag } from '../components/ui';
import { ProductEditDialog } from '../components/ProductEditDialog';
import { DepartmentsPanel } from '../components/DepartmentsPanel';
import { canWrite } from '../lib/mutate';

interface Product {
  productId: number;
  name: string;
  barcode: string;
  category: string;
  salePrice: string;
  stock: number;
  vatRate: string | null;
  flags: string[];
}

interface ProductPage {
  items: Product[];
  nextCursor: string | null;
}

interface Reference {
  categories: { categoryId: number; name: string; products: number }[];
}

interface CatalogueSummary {
  products: number;
  stockValue: string;
  reorder: number;
  overstock: number;
  deadstock: number;
  negativeStock: number;
}

const FLAGS = [
  { key: '', label: 'All' },
  { key: 'reorder', label: 'Reorder' },
  { key: 'overstock', label: 'Overstock' },
  { key: 'deadstock', label: 'Dead stock' },
  { key: 'negative', label: 'Negative' },
] as const;

export function Catalogue(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [flag, setFlag] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [view, setView] = useState<'products' | 'departments'>('products');

  // The server enforces this; hiding the control just avoids offering an action
  // that would be refused.
  const editable = canWrite(activeStore?.role);

  // Debounced, because the catalogue is 25,000 rows and a query per keystroke
  // would put a scan on the database for every letter typed.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: summary } = useQuery({
    queryKey: ['catalogue-summary', storeId],
    queryFn: () => api.get<CatalogueSummary>(`/stores/${storeId}/catalogue-summary`),
    enabled: Boolean(storeId),
  });

  const { data: reference } = useQuery({
    queryKey: ['reference', storeId],
    queryFn: () => api.get<Reference>(`/stores/${storeId}/reference`),
    enabled: Boolean(storeId),
    staleTime: 5 * 60_000,
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (search) params.set('search', search);
    if (categoryId) params.set('categoryId', categoryId);
    if (flag) params.set('flag', flag);
    return params.toString();
  }, [search, categoryId, flag]);

  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['products', storeId, queryString],
      queryFn: ({ pageParam }) =>
        api.get<ProductPage>(
          `/stores/${storeId}/products?${queryString}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      enabled: Boolean(storeId),
    });

  const products = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Catalogue</p>
          <h1 className="text-lg font-medium tracking-tight">
            {view === 'departments'
              ? 'Departments'
              : summary
                ? `${formatCount(summary.products)} products`
                : 'Products'}
          </h1>
        </div>

        <div className="flex gap-1" role="group" aria-label="Catalogue view">
          {(['products', 'departments'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setView(option)}
              aria-pressed={view === option}
              className={cn(
                'rounded border px-2.5 py-1 text-xs capitalize',
                view === option
                  ? 'border-accent bg-accent-soft font-medium text-accent'
                  : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </header>

      {view === 'departments' && storeId && <DepartmentsPanel storeId={storeId} />}

      {view === 'products' && (
        <>
      {summary && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Stat label="Stock value" value={formatCurrency(summary.stockValue)} />
          <Stat label="Reorder" value={formatCount(summary.reorder)} />
          <Stat label="Overstock" value={formatCount(summary.overstock)} />
          <Stat label="Dead stock" value={formatCount(summary.deadstock)} />
          <Stat
            label="Negative stock"
            value={formatCount(summary.negativeStock)}
            warn={summary.negativeStock > 0}
          />
        </div>
      )}

      {/* Filters in one row above the table. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-52 flex-1">
          <Search
            size={14}
            aria-hidden
            className="absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-faint"
          />
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name or barcode"
            aria-label="Search products"
            className="w-full rounded border border-rule bg-surface py-1.5 pr-2 pl-8 text-xs outline-none focus:border-accent"
          />
        </div>

        <select
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
          aria-label="Filter by category"
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs outline-none focus:border-accent"
        >
          <option value="">All departments</option>
          {reference?.categories.map((category) => (
            <option key={category.categoryId} value={category.categoryId}>
              {category.name} ({category.products})
            </option>
          ))}
        </select>

        {/* Wraps: five chips at a thumb-sized 40px minimum do not fit one line
            on a narrow phone, and without this the row pushed the whole page
            into a sideways scroll rather than folding. */}
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by stock state">
          {FLAGS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setFlag(option.key)}
              aria-pressed={flag === option.key}
              className={cn(
                'rounded border px-2 py-1.5 text-xs',
                flag === option.key
                  ? 'border-accent bg-accent-soft font-medium text-accent'
                  : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorNote error={error} />}

      <Panel>
        {isPending ? (
          <Spinner label="Loading products…" />
        ) : products.length === 0 ? (
          <EmptyState>No products match those filters.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">Product catalogue</caption>
              <thead>
                {/* Secondary columns are dropped below md rather than left to
                    scroll sideways. A six-column table in a 342px container
                    means the price and stock — the two things you open this
                    page for — are off screen until you swipe. */}
                <tr className="border-b border-rule">
                  <th className="qs-eyebrow py-2 pl-3 text-left">Product</th>
                  <th className="qs-eyebrow hidden py-2 text-left md:table-cell">Department</th>
                  <th className="qs-eyebrow py-2 text-right">Price</th>
                  <th className="qs-eyebrow hidden py-2 text-right md:table-cell">VAT</th>
                  {/* pr-3 on the stock column: without it the right-aligned
                      number sits flush against the State header and the tags
                      below, and "STOCK STATE" reads as one word. */}
                  <th className="qs-eyebrow py-2 pr-3 text-right">Stock</th>
                  <th className="qs-eyebrow hidden py-2 pr-3 text-left md:table-cell">State</th>
                  {editable && <th className="py-2 pr-3"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.productId} className="border-b border-rule last:border-0">
                    <td className="py-1.5 pl-3">
                      {/* 320px of product name does not fit beside price and
                          stock in a 342px container, which pushed the table
                          just past its bounds and reintroduced a sideways
                          scroll. Narrow on phones, generous from md up.

                          A share of the viewport rather than a fixed 9.5rem:
                          the three columns to the right of this one are the same
                          width on every phone, so a cap tuned to a 390px screen
                          is the one that overflows on a 360px one. */}
                      <p className="max-w-[34vw] truncate text-xs md:max-w-xs" title={product.name}>
                        {product.name}
                      </p>
                      <p className="qs-figure text-2xs text-ink-faint">{product.barcode}</p>
                      {/* The State column is hidden on small screens, so its
                          tags move in here rather than being lost. */}
                      {product.flags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1 md:hidden">
                          {product.flags.map((value) => (
                            <Tag key={value} value={value} />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="hidden max-w-40 truncate py-1.5 text-xs text-ink-muted md:table-cell">
                      {product.category}
                    </td>
                    <td className="qs-figure py-1.5 text-right text-xs">
                      {formatCurrency(product.salePrice)}
                    </td>
                    <td className="qs-figure hidden py-1.5 text-right text-2xs text-ink-muted md:table-cell">
                      {product.vatRate === null ? 'exempt' : `${product.vatRate}%`}
                    </td>
                    <td
                      className={cn(
                        'qs-figure py-1.5 pr-3 text-right text-xs whitespace-nowrap',
                        product.stock < 0 && 'text-down',
                      )}
                    >
                      {formatCount(product.stock)}
                    </td>
                    <td className="hidden py-1.5 pr-3 md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {product.flags.map((value) => (
                          <Tag key={value} value={value} />
                        ))}
                      </div>
                    </td>
                    {editable && (
                      <td className="py-1.5 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() => setEditing(product.productId)}
                          className="rounded border border-rule px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasNextPage && (
          <div className="border-t border-rule p-2 text-center">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:border-rule-strong hover:text-ink disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : `Load more (${products.length} shown)`}
            </button>
          </div>
        )}
      </Panel>
        </>
      )}

      {/* Mounted only while a product is being edited, so each open starts with
          a clean form — see `ProductEditDialog`. */}
      {storeId && editing !== null && (
        <ProductEditDialog
          storeId={storeId}
          productId={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function Stat({
  label,
  value,
  warn = false,
}: {
  label: string;
  value: string;
  warn?: boolean;
}): React.JSX.Element {
  return (
    <div className={cn('qs-panel p-3', warn && 'border-warn/30 bg-warn-soft')}>
      <p className={cn('qs-eyebrow', warn && 'text-warn')}>{label}</p>
      <p className={cn('qs-figure mt-1 text-lg leading-none font-medium', warn && 'text-warn')}>
        {value}
      </p>
    </div>
  );
}
